<!-- 无人机设备、任务、飞行、故障和维护列表展示。数据请求和分页由父页面处理。 -->
<template>
  <section class="drone-panel">
    <div class="summary-grid">
      <article v-for="stat in stats" :key="stat.label" class="summary-card">
        <div>
          <span>{{ stat.label }}</span>
          <strong>{{ stat.value }}</strong>
        </div>
        <small :class="`is-${stat.tone ?? 'brand'}`">{{ stat.sub }}</small>
      </article>
    </div>

    <article class="content-card">
      <div class="filter-bar">
        <template v-if="section === 'devices'">
          <el-input v-model="deviceFilters.keyword" clearable placeholder="搜索编号/名称" class="filter-control" @keyup.enter="emit('search')" />
          <div class="filter-row">
            <el-select v-model="deviceFilters.status" clearable placeholder="设备状态" class="filter-control">
              <el-option v-for="item in droneStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
            <el-select v-model="deviceFilters.online" clearable placeholder="在线状态" class="filter-control">
              <el-option label="在线" :value="1" />
              <el-option label="离线" :value="0" />
            </el-select>
          </div>
        </template>
        <template v-else-if="section === 'tasks'">
          <el-input v-model="taskFilters.keyword" clearable placeholder="搜索配送单/订单号" class="filter-control" @keyup.enter="emit('search')" />
          <el-select v-model="taskFilters.status" clearable placeholder="任务状态" class="filter-control">
            <el-option v-for="item in taskStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </template>
        <template v-else-if="section === 'flights'">
          <el-input v-model="flightFilters.keyword" clearable placeholder="搜索配送单/订单号" class="filter-control" @keyup.enter="emit('search')" />
          <div class="filter-row">
            <el-input v-model="flightFilters.droneNo" clearable placeholder="无人机编号" class="filter-control" @keyup.enter="emit('search')" />
            <el-select v-model="flightFilters.status" clearable placeholder="飞行状态" class="filter-control">
              <el-option v-for="item in taskStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </div>
        </template>
        <template v-else-if="section === 'faults'">
          <el-input v-model="faultFilters.droneId" clearable placeholder="无人机 ID" class="filter-control" @keyup.enter="emit('search')" />
          <div class="filter-row">
            <el-select v-model="faultFilters.status" clearable placeholder="故障状态" class="filter-control">
              <el-option v-for="item in faultStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
            <el-input v-model="faultFilters.level" clearable placeholder="故障等级" class="filter-control" @keyup.enter="emit('search')" />
          </div>
        </template>
        <template v-else>
          <el-input v-model="maintenanceFilters.droneId" clearable placeholder="无人机 ID" class="filter-control" @keyup.enter="emit('search')" />
          <div class="filter-row">
            <el-select v-model="maintenanceFilters.status" clearable placeholder="维护状态" class="filter-control">
              <el-option v-for="item in maintenanceStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
            <el-select v-model="maintenanceFilters.type" clearable placeholder="维护类型" class="filter-control">
              <el-option v-for="item in maintenanceTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </div>
        </template>
        <div class="filter-bar__actions">
          <button class="search-button" @click="emit('search')">搜索</button>
          <button class="reset-button" @click="emit('reset')">重置</button>
        </div>
      </div>

      <div v-if="section === 'devices'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>设备编号</th>
              <th>设备名称</th>
              <th>型号</th>
              <th>仓库编码</th>
              <th>所属门店</th>
              <th class="is-right">电量</th>
              <th>状态</th>
              <th>在线</th>
              <th>最后通信</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in dronePage?.list ?? []" :key="item.id">
              <td><span class="cell-text">{{ item.droneNo }}</span></td>
              <td><span class="cell-text">{{ item.name || '--' }}</span></td>
              <td><span class="cell-text">{{ item.model || '--' }}</span></td>
              <td><span class="cell-text">{{ item.warehouseCode || '--' }}</span></td>
              <td><span class="cell-text">{{ item.storeName || item.storeId || '--' }}</span></td>
              <td class="is-right">{{ item.battery ?? 0 }}%</td>
              <td><span :class="['status-tag', toneClass(droneStatusTone(item.status))]">{{ getDroneStatusText(item.status) }}</span></td>
              <td><span :class="['status-tag', toneClass(item.online ? 'success' : 'muted')]">{{ item.online ? '在线' : '离线' }}</span></td>
              <td>{{ item.lastCommAt || '--' }}</td>
              <td class="is-right">
                <div class="row-actions">
                  <button title="编辑设备" @click="emit('editDrone', item)"><el-icon><EditPen /></el-icon></button>
                  <button class="is-danger" title="删除设备" :disabled="deletingDroneId === item.id" @click="emit('removeDrone', item)"><el-icon><Delete /></el-icon></button>
                </div>
              </td>
            </tr>
            <tr v-if="!(dronePage?.list.length) && !loadingDrones"><td colspan="10"><el-empty description="暂无无人机设备" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'tasks'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>配送单号</th>
              <th>订单号</th>
              <th>门店</th>
              <th>无人机</th>
              <th>目的地</th>
              <th class="is-right">配送费</th>
              <th>状态</th>
              <th>预计到达</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in droneTaskPage?.list ?? []" :key="item.deliveryId">
              <td><span class="cell-text">{{ item.deliveryNo }}</span></td>
              <td><span class="cell-text">{{ item.orderNo }}</span></td>
              <td><span class="cell-text">{{ item.storeName || '--' }}</span></td>
              <td><span class="cell-text">{{ item.droneName || item.droneCode || '未指派' }}</span></td>
              <td><span class="cell-text">{{ item.toAddress || '--' }}</span></td>
              <td class="is-right">{{ formatAmount(item.fee) }}</td>
              <td><span :class="['status-tag', toneClass(droneTaskStatusTone(item.status))]">{{ getDroneTaskStatusText(item.status) }}</span></td>
              <td>{{ item.estimateArriveAt || '--' }}</td>
            </tr>
            <tr v-if="!(droneTaskPage?.list.length) && !loadingDroneTasks"><td colspan="8"><el-empty description="暂无无人机任务" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'flights'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>配送单号</th>
              <th>订单号</th>
              <th>无人机编号</th>
              <th>门店</th>
              <th>飞行时长</th>
              <th class="is-right">轨迹点</th>
              <th>状态</th>
              <th>完成时间</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in droneFlightPage?.list ?? []" :key="item.deliveryId">
              <td><span class="cell-text">{{ item.deliveryNo }}</span></td>
              <td><span class="cell-text">{{ item.orderNo }}</span></td>
              <td><span class="cell-text">{{ item.droneCode || '--' }}</span></td>
              <td><span class="cell-text">{{ item.storeName || '--' }}</span></td>
              <td>{{ item.flightDuration || '--' }}</td>
              <td class="is-right">{{ item.waypointCount ?? 0 }}</td>
              <td><span :class="['status-tag', toneClass(droneTaskStatusTone(item.status))]">{{ getDroneTaskStatusText(item.status) }}</span></td>
              <td>{{ item.deliveredAt || '--' }}</td>
            </tr>
            <tr v-if="!(droneFlightPage?.list.length) && !loadingDroneFlights"><td colspan="8"><el-empty description="暂无飞行记录" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'faults'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>故障单号</th>
              <th>设备编号</th>
              <th>设备名称</th>
              <th>故障类型</th>
              <th>等级</th>
              <th>状态</th>
              <th>说明</th>
              <th>上报时间</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in droneFaultPage?.list ?? []" :key="item.id">
              <td><span class="cell-text">{{ item.faultNo }}</span></td>
              <td><span class="cell-text">{{ item.droneNo }}</span></td>
              <td><span class="cell-text">{{ item.droneName || '--' }}</span></td>
              <td><span class="cell-text">{{ item.type }}</span></td>
              <td>{{ item.level || '--' }}</td>
              <td><span :class="['status-tag', toneClass(droneFaultStatusTone(item.status))]">{{ getDroneFaultStatusText(item.status) }}</span></td>
              <td><span class="cell-text">{{ item.description || item.resolveNote || '--' }}</span></td>
              <td>{{ item.createdAt || '--' }}</td>
            </tr>
            <tr v-if="!(droneFaultPage?.list.length) && !loadingDroneFaults"><td colspan="8"><el-empty description="暂无故障记录" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>维护单号</th>
              <th>设备编号</th>
              <th>设备名称</th>
              <th>类型</th>
              <th>状态</th>
              <th class="is-right">费用</th>
              <th>计划时间</th>
              <th>完成时间</th>
              <th>操作人</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in droneMaintenancePage?.list ?? []" :key="item.id">
              <td><span class="cell-text">{{ item.maintenanceNo }}</span></td>
              <td><span class="cell-text">{{ item.droneNo }}</span></td>
              <td><span class="cell-text">{{ item.droneName || '--' }}</span></td>
              <td>{{ getDroneMaintenanceTypeText(item.type) }}</td>
              <td><span :class="['status-tag', toneClass(droneMaintenanceStatusTone(item.status))]">{{ getDroneMaintenanceStatusText(item.status) }}</span></td>
              <td class="is-right">{{ formatAmount(item.cost) }}</td>
              <td>{{ item.scheduledAt || '--' }}</td>
              <td>{{ item.doneAt || '--' }}</td>
              <td>{{ item.operator || '--' }}</td>
            </tr>
            <tr v-if="!(droneMaintenancePage?.list.length) && !loadingDroneMaintenances"><td colspan="9"><el-empty description="暂无维护记录" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-if="currentPage" class="table-footer">
        <span>共 {{ currentPage.total }} 条记录，第 {{ currentPage.page }}/{{ totalPages }} 页</span>
        <div>
          <button :disabled="currentPage.page <= 1" @click="emit('changePage', currentPage.page - 1)">上一页</button>
          <button class="is-active">{{ currentPage.page }}</button>
          <button :disabled="!currentPage.hasMore" @click="emit('changePage', currentPage.page + 1)">下一页</button>
        </div>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import { computed, toRefs } from 'vue'
import { EditPen, Delete } from '@element-plus/icons-vue'
import type { OperationStat } from '@/data/operationDesign'
import type { DroneFaultListParams, DroneFlightListParams, DroneListParams, DroneMaintenanceListParams, DroneTaskListParams } from '@/api/drone'

type DronePage = Awaited<ReturnType<typeof import('@/api/drone').fetchDrones>>
type DroneTaskPage = Awaited<ReturnType<typeof import('@/api/drone').fetchDroneTasks>>
type DroneFlightPage = Awaited<ReturnType<typeof import('@/api/drone').fetchDroneFlights>>
type DroneFaultPage = Awaited<ReturnType<typeof import('@/api/drone').fetchDroneFaults>>
type DroneMaintenancePage = Awaited<ReturnType<typeof import('@/api/drone').fetchDroneMaintenances>>
type DroneSection = 'devices' | 'tasks' | 'flights' | 'faults' | 'maintenances'
type StatusTone = 'brand' | 'success' | 'warning' | 'error' | 'muted'

const props = defineProps<{
  section: DroneSection
  stats: OperationStat[]
  dronePage?: DronePage
  droneTaskPage?: DroneTaskPage
  droneFlightPage?: DroneFlightPage
  droneFaultPage?: DroneFaultPage
  droneMaintenancePage?: DroneMaintenancePage
  deviceFilters: DroneListParams
  taskFilters: DroneTaskListParams
  flightFilters: DroneFlightListParams
  faultFilters: DroneFaultListParams
  maintenanceFilters: DroneMaintenanceListParams
  loadingDrones: boolean
  loadingDroneTasks: boolean
  loadingDroneFlights: boolean
  loadingDroneFaults: boolean
  loadingDroneMaintenances: boolean
  deletingDroneId: string
  totalPages: number
  formatAmount: (amountInFen: number) => string
  getDroneStatusText: (status: string) => string
  getDroneTaskStatusText: (status: string) => string
  getDroneFaultStatusText: (status: string) => string
  getDroneMaintenanceStatusText: (status: string) => string
  getDroneMaintenanceTypeText: (type: string) => string
  droneStatusTone: (status: string) => StatusTone
  droneTaskStatusTone: (status: string) => StatusTone
  droneFaultStatusTone: (status: string) => StatusTone
  droneMaintenanceStatusTone: (status: string) => StatusTone
  toneClass: (tone: StatusTone) => string
}>()

const emit = defineEmits<{
  search: []
  reset: []
  changePage: [page: number]
  editDrone: [drone: DronePage['list'][0]]
  removeDrone: [drone: DronePage['list'][0]]
}>()

const {
  section,
  stats,
  dronePage,
  droneTaskPage,
  droneFlightPage,
  droneFaultPage,
  droneMaintenancePage,
  deviceFilters,
  taskFilters,
  flightFilters,
  faultFilters,
  maintenanceFilters,
  loadingDrones,
  loadingDroneTasks,
  loadingDroneFlights,
  loadingDroneFaults,
  loadingDroneMaintenances,
  deletingDroneId,
  totalPages,
  formatAmount,
  getDroneStatusText,
  getDroneTaskStatusText,
  getDroneFaultStatusText,
  getDroneMaintenanceStatusText,
  getDroneMaintenanceTypeText,
  droneStatusTone,
  droneTaskStatusTone,
  droneFaultStatusTone,
  droneMaintenanceStatusTone,
  toneClass,
} = toRefs(props)

const currentPage = computed(() => {
  if (section.value === 'devices') return dronePage.value
  if (section.value === 'tasks') return droneTaskPage.value
  if (section.value === 'flights') return droneFlightPage.value
  if (section.value === 'faults') return droneFaultPage.value
  return droneMaintenancePage.value
})

const droneStatusOptions = [
  { label: '空闲', value: 'IDLE' },
  { label: '任务中', value: 'TASK' },
  { label: '充电中', value: 'CHARGING' },
  { label: '维护中', value: 'MAINTENANCE' },
  { label: '故障', value: 'FAULT' },
  { label: '离线', value: 'OFFLINE' },
]
const taskStatusOptions = [
  { label: '待调度', value: 'WAITING' },
  { label: '装载中', value: 'DRONE_LOADING' },
  { label: '已起飞', value: 'DRONE_TAKEOFF' },
  { label: '配送中', value: 'DRONE_FLYING' },
  { label: '已到达', value: 'ARRIVED' },
  { label: '已完成', value: 'COMPLETED' },
  { label: '异常', value: 'EXCEPTION' },
]
const faultStatusOptions = [
  { label: '待处理', value: 'OPEN' },
  { label: '处理中', value: 'PROCESSING' },
  { label: '已解决', value: 'RESOLVED' },
]
const maintenanceStatusOptions = [
  { label: '计划中', value: 'SCHEDULED' },
  { label: '执行中', value: 'DOING' },
  { label: '已完成', value: 'DONE' },
]
const maintenanceTypeOptions = [
  { label: '维修', value: 'REPAIR' },
  { label: '保养', value: 'MAINTAIN' },
  { label: '巡检', value: 'INSPECT' },
]
</script>

<style scoped lang="scss">
.drone-panel { display: flex; flex-direction: column; gap: 16px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.summary-card { min-height: 102px; padding: 20px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); }
.summary-card div { min-width: 0; }
.summary-card span { display: block; margin-bottom: 8px; color: var(--jf-color-text-secondary); font-size: 14px; }
.summary-card strong { color: var(--jf-color-text-primary); font-size: 26px; line-height: 1.1; word-break: break-word; }
.summary-card small { padding: 4px 8px; border-radius: 6px; background: var(--jf-color-background); font-size: 12px; white-space: nowrap; }
.content-card { border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); overflow: hidden; }
.filter-bar { min-height: 66px; padding: 14px 16px; border-bottom: 1px solid var(--jf-color-border); display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.filter-control { width: 220px; }
.filter-row { display: flex; gap: 12px; }
.filter-row .filter-control { min-width: 124px; }
.filter-bar__actions { display: flex; gap: 8px; margin-left: auto; }
button { border: 0; cursor: pointer; font: inherit; }
.search-button, .reset-button { height: 36px; padding: 0 16px; border-radius: 8px; font-size: 14px; font-weight: 700; }
.search-button { background: var(--jf-color-primary); color: #fff; }
.reset-button { border: 1px solid var(--jf-color-border); background: #fff; color: var(--jf-color-text-primary); }
.table-wrap { width: 100%; overflow-x: auto; }
table { width: 100%; border-collapse: collapse; white-space: nowrap; font-size: 14px; }
thead { background: rgba(252, 250, 246, .72); color: var(--jf-color-text-secondary); font-size: 12px; }
th, td { padding: 14px 16px; text-align: left; border-bottom: 1px solid var(--jf-color-border); }
th { font-weight: 700; }
tbody tr:hover { background: rgba(252, 250, 246, .75); }
.is-right { text-align: right; }
.cell-text { display: inline-block; max-width: 220px; overflow: hidden; text-overflow: ellipsis; vertical-align: middle; }
.status-tag { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 999px; font-size: 12px; font-weight: 700; }
.is-brand { background: rgba(118, 183, 232, .14); color: var(--jf-color-primary-dark); }
.is-success { background: rgba(69, 185, 124, .12); color: var(--jf-color-success); }
.is-warning { background: rgba(242, 169, 80, .14); color: var(--jf-color-warning); }
.is-error { background: rgba(231, 101, 101, .12); color: var(--jf-color-error); }
.is-muted { background: #f1f3f5; color: var(--jf-color-text-secondary); }
.row-actions { display: inline-flex; justify-content: flex-end; gap: 6px; color: var(--jf-color-primary-dark); }
.row-actions button { width: 28px; height: 28px; border-radius: 6px; display: grid; place-items: center; background: transparent; color: inherit; }
.row-actions button:hover { background: rgba(179, 218, 247, .3); }
.row-actions button.is-danger { color: var(--jf-color-error); }
.row-actions button.is-danger:hover { background: rgba(231, 101, 101, .12); }
.row-actions button:disabled { cursor: not-allowed; opacity: .5; }
.table-footer { padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; gap: 16px; color: var(--jf-color-text-secondary); font-size: 14px; }
.table-footer div { display: flex; gap: 6px; }
.table-footer button { min-width: 32px; height: 32px; padding: 0 10px; border: 1px solid var(--jf-color-border); border-radius: 8px; background: #fff; color: var(--jf-color-text-secondary); }
.table-footer .is-active { border-color: var(--jf-color-primary); background: var(--jf-color-primary); color: #fff; }
.table-footer button:disabled { cursor: not-allowed; opacity: .5; }
@media (max-width: 1180px) {
  .summary-grid { grid-template-columns: 1fr; }
  .filter-control { width: 100%; }
  .filter-bar__actions { width: 100%; margin-left: 0; }
}
</style>
